import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChatBubbleLeftRightIcon, UserGroupIcon, ChartBarIcon, AcademicCapIcon, SparklesIcon, CheckCircleIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';

const Home = () => {
  const { user } = useAuth();
  const [tariffs, setTariffs] = useState([]);
  const [freeTariffInfo, setFreeTariffInfo] = useState(null);
  const navigate = useNavigate();

  const handleBuyTariff = async (tariff) => {
    if (!user) {
      // Сохраняем выбранный тариф и переходим на регистрацию
      localStorage.setItem('pending_tariff', JSON.stringify(tariff));
      navigate('/register');
      return;
    }
    if (tariff.price_per_month > 0) {
      try {
        const token = localStorage.getItem('access_token');
        const res = await fetch('/api/payments/create', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ 
            amount: tariff.price_per_month, 
            tariff_id: tariff.id, 
            for_organization: tariff.for_organization || false,
            return_url: window.location.origin + (tariff.for_organization ? '/manager' : '/profile') 
          })
        });
        const data = await res.json();
        if (data.confirmation_url) {
          window.location.href = data.confirmation_url;
        } else {
          toast.error(data.error || 'Ошибка создания платежа');
        }
      } catch (e) {
        toast.error('Ошибка подключения к платежной системе');
      }
    } else {
      try {
        const token = localStorage.getItem('access_token');
        const res = await fetch('/api/payments/tariffs/change', {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({ tariff_id: tariff.id })
        });
        if (res.ok) {
          toast.success('Тариф активирован');
          navigate('/profile');
        } else {
          const err = await res.json();
          toast.error(err.error || 'Ошибка выбора тарифа');
        }
      } catch (e) {
        toast.error('Ошибка выбора тарифа');
      }
    }
  };

  useEffect(() => {
    fetch('/api/auth/tariffs/public').then(r => r.json()).then(data => {
      console.log('API response:', data);
      if (data.tariffs && Array.isArray(data.tariffs)) {
        setTariffs(data.tariffs);
        setFreeTariffInfo(data.free_tariff_info);
      } else if (Array.isArray(data)) {
        setTariffs(data);
      }
    }).catch(err => {
      console.error('API error:', err);
    });
  }, []);

  const features = [
    { icon: ChatBubbleLeftRightIcon, title: 'Реалистичные диалоги', desc: 'Практикуйте общение с виртуальными клиентами в различных сценариях', color: 'blue' },
    { icon: ChartBarIcon, title: 'Детальная аналитика', desc: 'Отслеживайте прогресс и получайте персонализированные рекомендации', color: 'purple' },
    { icon: AcademicCapIcon, title: 'Обучение на практике', desc: 'Учитесь на своих ошибках с мгновенной обратной связью от ИИ', color: 'green' },
    { icon: UserGroupIcon, title: 'Для команд', desc: 'Тренируйте целые отделы с единой системой отчётности', color: 'orange' },
  ];

  const steps = [
    { num: '01', title: 'Выберите сценарий', desc: 'Подберите подходящую ситуацию для тренировки' },
    { num: '02', title: 'Начните диалог', desc: 'Общайтесь с ИИ-клиентом как с реальным человеком' },
    { num: '03', title: 'Получите анализ', desc: 'Узнайте свои сильные стороны и точки роста' },
    { num: '04', title: 'Совершенствуйтесь', desc: 'Практикуйтесь регулярно для лучших результатов' },
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800"></div>
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-32">
          <div className="flex flex-col lg:flex-row items-center gap-12">
            <div className="flex-1 text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
                <SparklesIcon className="w-5 h-5 text-yellow-300" />
                <span className="text-white/90 text-sm font-medium">Тренажёр на базе ИИ</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Прокачайте навыки<br />
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-200 to-purple-200">общения с клиентами</span>
              </h1>
              <p className="text-xl text-blue-100 mb-6 max-w-xl mx-auto lg:mx-0">
                Тренируйтесь в реалистичных сценариях с виртуальными клиентами. Получайте мгновенную обратную связь и улучшайте результаты.
              </p>
              {!user && (
                <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 mb-8 max-w-xl mx-auto lg:mx-0">
                  <p className="text-white font-semibold text-lg">
                    🎁 Зарегистрируйтесь и получите <span className="text-yellow-300">
                      {freeTariffInfo && freeTariffInfo.monthly_limit > 0 
                        ? `${freeTariffInfo.monthly_limit.toLocaleString()} токенов` 
                        : '1000 токенов'}
                    </span> для тренировок!
                  </p>
                </div>
              )}
              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                {!user ? (
                  <Link to="/register" className="inline-flex items-center justify-center gap-2 bg-white text-blue-700 font-bold text-lg px-8 py-4 rounded-xl hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl hover:scale-105">
                    Зарегистрироваться
                    <ArrowRightIcon className="w-5 h-5" />
                  </Link>
                ) : (
                  <Link to="/scenarios" className="inline-flex items-center justify-center gap-2 bg-white text-blue-700 font-bold text-lg px-8 py-4 rounded-xl hover:bg-blue-50 transition-all shadow-lg hover:shadow-xl hover:scale-105">
                    Начать тренировку
                    <ArrowRightIcon className="w-5 h-5" />
                  </Link>
                )}
              </div>
            </div>
            <div className="flex-1 flex justify-center">
              <img src="/buzilla_universal.svg" alt="Бузилла" className="w-64 sm:w-80 lg:w-96 drop-shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">Возможности платформы</h2>
            <p className="text-xl text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">Всё необходимое для эффективного обучения коммуникациям</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((f, idx) => (
              <div key={idx} className="bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all hover:-translate-y-1">
                <div className={`w-14 h-14 rounded-xl bg-${f.color}-100 dark:bg-${f.color}-900/30 flex items-center justify-center mb-5`}>
                  <f.icon className={`w-7 h-7 text-${f.color}-600 dark:text-${f.color}-400`} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">{f.title}</h3>
                <p className="text-gray-600 dark:text-gray-400">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">Как это работает</h2>
            <p className="text-xl text-gray-600 dark:text-gray-400">Четыре простых шага к улучшению навыков</p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map((step, idx) => (
              <div key={idx} className="relative">
                {idx < 3 && <div className="hidden lg:block absolute top-8 left-full w-full h-0.5 bg-gradient-to-r from-blue-300 to-transparent -translate-x-4"></div>}
                <div className="text-center">
                  <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-indigo-600 text-white text-2xl font-bold mb-4 shadow-lg">
                    {step.num}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">{step.title}</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* B2C Tariffs Section */}
      {tariffs.filter(t => t.type !== 'b2b').length > 0 && (
        <section className="py-20 bg-white dark:bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">Для частных клиентов</h2>
              <p className="text-xl text-gray-600 dark:text-gray-400">Индивидуальные тарифы для личного развития</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {tariffs.filter(t => t.type !== 'b2b').map((t, idx) => (
                <div key={t.id} className={`bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all flex flex-col border border-gray-200 dark:border-gray-700 ${idx === 0 ? 'ring-2 ring-blue-400 scale-105' : ''}`}>
                  {idx === 0 && <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-blue-500 text-white text-sm font-bold px-4 py-1 rounded-full">Популярный</div>}
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">{t.name}</h3>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-4xl font-bold text-gray-900 dark:text-white">{t.price_per_month > 0 ? `${t.price_per_month}₽` : 'Бесплатно'}</span>
                  </div>
                  {t.description && <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">{t.description}</p>}
                  <ul className="space-y-3 mb-8 flex-grow">
                    <li className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                      <CheckCircleIcon className="w-5 h-5 text-green-500" />
                      {t.monthly_limit > 0 ? `${t.monthly_limit.toLocaleString()} токенов` : 'Безлимитное общение'}
                    </li>
                    {t.daily_limit > 0 && (
                      <li className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                        <CheckCircleIcon className="w-5 h-5 text-green-500" />
                        До {t.daily_limit.toLocaleString()} токенов в день
                      </li>
                    )}
                    <li className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
                      <CheckCircleIcon className="w-5 h-5 text-green-500" />
                      Срок действия: {t.period_days || 30} дней
                    </li>
                  </ul>
                  <button onClick={() => handleBuyTariff(t)} className="block w-full py-3 rounded-xl font-semibold bg-blue-600 text-white hover:bg-blue-700 transition-all text-center mt-auto">
                    {t.price_per_month > 0 ? 'Купить' : 'Выбрать'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* B2B Tariffs Section */}
      {tariffs.filter(t => t.type === 'b2b').length > 0 && (
        <section className="py-20 bg-gray-50 dark:bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-4">Для бизнеса</h2>
              <p className="text-xl text-gray-600 dark:text-gray-400">Корпоративные решения для команд</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {tariffs.filter(t => t.type === 'b2b').map((t, idx) => (
                <div key={t.id} className={`bg-gradient-to-br from-purple-600 to-indigo-700 rounded-2xl p-8 text-white shadow-lg hover:shadow-xl transition-all flex flex-col ${idx === 0 ? 'ring-2 ring-yellow-400 scale-105' : ''}`}>
                  {idx === 0 && <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-yellow-400 text-purple-900 text-sm font-bold px-4 py-1 rounded-full">Популярный</div>}
                  <h3 className="text-2xl font-bold mb-2">{t.name}</h3>
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-4xl font-bold">{t.price_per_month > 0 ? `${t.price_per_month}₽` : 'Бесплатно'}</span>
                  </div>
                  {t.description && <p className="text-purple-200 text-sm mb-4">{t.description}</p>}
                  <ul className="space-y-3 mb-8 flex-grow">
                    <li className="flex items-center gap-2 text-purple-100">
                      <CheckCircleIcon className="w-5 h-5" />
                      {t.monthly_limit > 0 ? `${t.monthly_limit.toLocaleString()} токенов` : 'Безлимитное общение'}
                    </li>
                    {t.daily_limit > 0 && (
                      <li className="flex items-center gap-2 text-purple-100">
                        <CheckCircleIcon className="w-5 h-5" />
                        До {t.daily_limit.toLocaleString()} токенов в день
                      </li>
                    )}
                    <li className="flex items-center gap-2 text-purple-100">
                      <CheckCircleIcon className="w-5 h-5" />
                      Срок действия: {t.period_days || 30} дней
                    </li>
                    {t.participant_limit && (
                      <li className="flex items-center gap-2 text-purple-100">
                        <CheckCircleIcon className="w-5 h-5" />
                        До {t.participant_limit} пользователей
                      </li>
                    )}
                  </ul>
                  {user && user.organization_id ? (
                    <button onClick={() => handleBuyTariff({...t, for_organization: true})} className="block w-full py-3 rounded-xl font-semibold bg-white text-purple-700 hover:bg-purple-50 transition-all text-center mt-auto">
                      Купить
                    </button>
                  ) : (
                    <button 
                      onClick={() => {
                        localStorage.setItem('pending_tariff', JSON.stringify({...t, for_organization: true}));
                        navigate('/register/business');
                      }} 
                      className="block w-full py-3 rounded-xl font-semibold bg-white text-purple-700 hover:bg-purple-50 transition-all text-center mt-auto"
                    >
                      Зарегистрироваться
                    </button>
                  )}
                </div>
                  ))}
                </div>
            
            {/* Contact Section */}
            <div className="mt-16">
              <div className="bg-white border border-gray-200 rounded-2xl p-8 md:p-10 shadow-lg">
                <div className="text-center mb-8">
                  <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                    Вам нужно заключить договор?
                  </h3>
                  <p className="text-lg text-gray-600">
                    Пожалуйста, свяжитесь с нами
                  </p>
                </div>
                
                <div className="flex justify-center">
                  <a 
                    href="mailto:info@profdialog.ru" 
                    className="flex items-center gap-4 bg-gray-50 hover:bg-gray-100 px-6 py-4 rounded-xl transition-colors group"
                  >
                    <div className="w-12 h-12 bg-gray-500 rounded-full flex items-center justify-center group-hover:bg-gray-600 transition-colors">
                      <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/>
                        <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/>
                      </svg>
                    </div>
                    <span className="text-lg font-semibold text-gray-900">info@profdialog.ru</span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      {!user && (
        <section className="relative py-20 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-blue-700 via-blue-800 to-indigo-800"></div>
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-white rounded-full blur-3xl"></div>
          </div>
          <div className="relative max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-3xl sm:text-5xl font-bold text-white mb-6">Готовы начать?</h2>
            <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
              Присоединяйтесь к тысячам пользователей, которые уже улучшают свои навыки общения с клиентами
            </p>
            <Link to="/register" className="inline-flex items-center justify-center gap-2 bg-white text-blue-700 font-bold text-lg px-10 py-5 rounded-xl hover:bg-blue-50 transition-all shadow-xl hover:shadow-2xl hover:scale-105">
              Зарегистрироваться бесплатно
              <ArrowRightIcon className="w-5 h-5" />
            </Link>
          </div>
        </section>
      )}
    </div>
  );
};

export default Home;
